import { useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserRound, Mail, Send, ShieldCheck, RefreshCw, Clock, Trash2, ShieldOff } from "lucide-react";

interface StaffUser {
  clerkId: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  createdAt: string;
}

interface PendingInvitation {
  id: string;
  emailAddress: string;
  createdAt: string;
}

const USERS_KEY = ["/api/users"];
const INVITATIONS_KEY = ["/api/users/invitations"];

async function fetchStaffUsers(): Promise<StaffUser[]> {
  const res = await fetch("/api/users", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load users");
  return res.json();
}

async function fetchInvitations(): Promise<PendingInvitation[]> {
  const res = await fetch("/api/users/invitations", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load invitations");
  return res.json();
}

async function inviteUser(email: string): Promise<void> {
  const res = await fetch("/api/users/invite", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? "Failed to send invitation"), { status: res.status });
  }
}

async function promoteUser(email: string): Promise<void> {
  const res = await fetch("/api/users/promote", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? "Failed to promote user"), { status: res.status });
  }
}

async function demoteUser(clerkId: string): Promise<void> {
  const res = await fetch("/api/users/demote", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ clerkId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? "Failed to remove staff access"), { status: res.status });
  }
}

async function revokeInvitation(id: string): Promise<void> {
  const res = await fetch(`/api/users/invite/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? "Failed to revoke invitation"), { status: res.status });
  }
}

export default function StaffUsersPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [inviteEmail, setInviteEmail] = useState("");
  const [promoteEmail, setPromoteEmail] = useState("");

  const { data: users, isLoading: usersLoading, isError: usersError } = useQuery({
    queryKey: USERS_KEY,
    queryFn: fetchStaffUsers,
  });

  const { data: invitations, isLoading: invitationsLoading, isError: invitationsError } = useQuery({
    queryKey: INVITATIONS_KEY,
    queryFn: fetchInvitations,
  });

  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      toast({ title: "Invitation sent", description: `An invite has been sent to ${inviteEmail}.` });
      setInviteEmail("");
      queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY });
    },
    onError: (err: any) => {
      toast({ title: "Invite failed", description: err.message, variant: "destructive" });
    },
  });

  const promoteMutation = useMutation({
    mutationFn: promoteUser,
    onSuccess: () => {
      toast({ title: "Staff access granted", description: `${promoteEmail} now has staff access.` });
      setPromoteEmail("");
      queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (err: any) => {
      toast({ title: "Promotion failed", description: err.message, variant: "destructive" });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      toast({ title: "Invitation revoked", description: "The pending invitation has been cancelled." });
      queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY });
    },
    onError: (err: any) => {
      toast({ title: "Revoke failed", description: err.message, variant: "destructive" });
    },
  });

  const demoteMutation = useMutation({
    mutationFn: demoteUser,
    onSuccess: () => {
      toast({ title: "Staff access removed", description: "The user no longer has staff access." });
      queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
    onError: (err: any) => {
      toast({ title: "Demote failed", description: err.message, variant: "destructive" });
    },
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    inviteMutation.mutate(email);
  };

  const handlePromote = (e: React.FormEvent) => {
    e.preventDefault();
    const email = promoteEmail.trim();
    if (!email) return;
    promoteMutation.mutate(email);
  };

  const handleRefreshAll = () => {
    queryClient.invalidateQueries({ queryKey: USERS_KEY });
    queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY });
  };

  return (
    <StaffLayout>
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Staff Users</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage who has access to the staff panel. Invite new users or grant access to existing Clerk accounts.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Send className="w-4 h-4 text-primary" />
                Invite New User
              </CardTitle>
              <CardDescription>
                Sends a Clerk invitation email. The recipient gets staff access automatically when they sign up.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="invite-email">Email address</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={inviteMutation.isPending || !inviteEmail.trim()}
                >
                  {inviteMutation.isPending ? "Sending…" : "Send Invitation"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="w-4 h-4 text-primary" />
                Promote Existing Account
              </CardTitle>
              <CardDescription>
                If someone already has a Clerk account (e.g. from a personal Gmail), grant them staff access directly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePromote} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="promote-email">Email address</Label>
                  <Input
                    id="promote-email"
                    type="email"
                    placeholder="existing@gmail.com"
                    value={promoteEmail}
                    onChange={(e) => setPromoteEmail(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full"
                  disabled={promoteMutation.isPending || !promoteEmail.trim()}
                >
                  {promoteMutation.isPending ? "Granting…" : "Grant Staff Access"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Pending Invitations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-4 h-4 text-primary" />
                Pending Invitations
              </CardTitle>
              <CardDescription className="mt-1">
                Invites that have been sent but not yet accepted.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => queryClient.invalidateQueries({ queryKey: INVITATIONS_KEY })}
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {invitationsLoading && (
              <div className="space-y-3">
                {[...Array(2)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            )}
            {invitationsError && (
              <p className="text-sm text-destructive">Failed to load pending invitations. Try refreshing.</p>
            )}
            {!invitationsLoading && !invitationsError && invitations && invitations.length === 0 && (
              <p className="text-sm text-slate-500">No pending invitations.</p>
            )}
            {!invitationsLoading && !invitationsError && invitations && invitations.length > 0 && (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {invitations.map((inv) => (
                  <li key={inv.id} className="flex items-center gap-3 py-3">
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-full p-2 shrink-0">
                      <Mail className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {inv.emailAddress}
                      </p>
                      <p className="text-xs text-slate-400">
                        Sent {new Date(inv.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => revokeMutation.mutate(inv.id)}
                      disabled={revokeMutation.isPending}
                      title="Revoke invitation"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Revoke
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Current Staff Members */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <UserRound className="w-4 h-4 text-primary" />
                Current Staff Members
              </CardTitle>
              <CardDescription className="mt-1">
                All accounts with staff access in this Clerk instance.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefreshAll}
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {usersLoading && (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            )}
            {usersError && (
              <p className="text-sm text-destructive">Failed to load staff users. Try refreshing.</p>
            )}
            {!usersLoading && !usersError && users && users.length === 0 && (
              <p className="text-sm text-slate-500">No staff users found.</p>
            )}
            {!usersLoading && !usersError && users && users.length > 0 && (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => (
                  <li key={u.clerkId} className="flex items-center gap-3 py-3">
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-full p-2 shrink-0">
                      <UserRound className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {u.name || u.firstName || "—"}
                      </p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 shrink-0" />
                        {u.email || "No email"}
                      </p>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => demoteMutation.mutate(u.clerkId)}
                      disabled={demoteMutation.isPending}
                      title="Remove staff access"
                    >
                      <ShieldOff className="w-4 h-4 mr-1" />
                      Remove access
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </StaffLayout>
  );
}
